/**
 * Web-push subscription plumbing for the driver PWA.
 *
 * The server half (VAPID transport, session emitter, /me/push routes)
 * shipped first and had no client at all — the VAPID keys were dead
 * config and the emitter could never deliver to anyone. This is the
 * half that gives it subscribers.
 *
 * ── What the platforms actually require ─────────────────────────────
 * iOS/iPadOS 16.4+ support web push, with two hard conditions that
 * shape this whole module:
 *
 *   1. The app MUST be installed to the Home Screen. Push is
 *      unavailable in the Safari tab — `window.Notification` is not
 *      even defined there on older iOS. So `pushSupport()` reports
 *      'needs-install' rather than 'unsupported', because the driver
 *      has something to DO about it.
 *   2. `Notification.requestPermission()` must be called from a user
 *      gesture. Not from an effect, not from a timer. Every caller
 *      here is a click handler.
 *
 * Android/desktop Chromium has neither restriction, but honouring both
 * everywhere keeps one code path.
 */

import { subscribePush, unsubscribePush, dismissPushPrompt } from './api';
import { isStandalone, isIosSafari } from './pwaInstall';

/** Base64url (VAPID public key) → the Uint8Array the API demands. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** The VAPID public key baked in at build time. Absent = push unarmed. */
export const VAPID_PUBLIC_KEY: string = import.meta.env['VITE_VAPID_PUBLIC_KEY'] ?? '';

export type PushSupport =
  /** Everything is present; we can ask for permission. */
  | 'ready'
  /** iOS Safari in a browser tab: push works only once installed. */
  | 'needs-install'
  /** No service worker / PushManager / Notification, or no VAPID key. */
  | 'unsupported';

/**
 * Can this browser, right now, subscribe to push?
 *
 * Deliberately does NOT consider the permission state — that is
 * `Notification.permission`, and a caller wants the two separately (a
 * denied permission is a different UI from an unsupported browser).
 */
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported';
  if (!VAPID_PUBLIC_KEY) return 'unsupported';
  const hasApis =
    'serviceWorker' in navigator
    && 'PushManager' in window
    && typeof window.Notification !== 'undefined';
  if (hasApis) return 'ready';
  // iOS Safari in a tab has no PushManager; installed, it does. Tell
  // the driver the actionable thing rather than "unsupported".
  if (isIosSafari() && !isStandalone()) return 'needs-install';
  return 'unsupported';
}

/** Current OS-level permission, normalised for browsers without the API. */
export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
    return 'unsupported';
  }
  return window.Notification.permission;
}

/** Is this device already registered with the server? */
export async function currentSubscription(): Promise<PushSubscription | null> {
  try {
    if (!('serviceWorker' in navigator)) return null;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export type EnableResult =
  | 'enabled'
  /** The driver said no in the OS sheet. Terminal until they change it
   *  in browser settings — the API refuses to ask twice. */
  | 'denied'
  /** Dismissed without choosing, or the tab lost the gesture. */
  | 'dismissed'
  | 'unsupported'
  /** Subscribed with the push service but the server rejected it. */
  | 'server-error';

/**
 * Ask for permission and register this device. MUST be called from a
 * user gesture (see the module header).
 *
 * The service worker registration is awaited rather than assumed:
 * `main.tsx` registers on window load, and a driver can tap the enable
 * button before that resolves.
 */
export async function enablePush(): Promise<EnableResult> {
  if (pushSupport() !== 'ready') return 'unsupported';

  try {
    const permission = await window.Notification.requestPermission();
    if (permission === 'denied') return 'denied';
    if (permission !== 'granted') return 'dismissed';

    const reg = await navigator.serviceWorker.ready;

    // Reuse an existing subscription when the browser already has one:
    // re-subscribing with the same key returns the same endpoint, but
    // going through getSubscription() first avoids a needless round
    // trip to the push service.
    const existing = await reg.pushManager.getSubscription();
    const sub = existing ?? (await reg.pushManager.subscribe({
      // Required by every browser: a push must always be user-visible.
      // Silent push is not available to web apps.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    }));

    const json = sub.toJSON();
    if (!json.keys?.p256dh || !json.keys?.auth) return 'server-error';

    await subscribePush({
      endpoint: sub.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      user_agent: navigator.userAgent.slice(0, 400),
    });
    return 'enabled';
  } catch {
    // requestPermission throws in some embedded webviews; subscribe
    // throws on a key mismatch or a push-service outage.
    return 'server-error';
  }
}

/**
 * Re-send THIS device's existing registration to the server, with no prompt
 * and no new subscription. For after a fleet join by email, which signs out
 * other devices by dropping every push registration on the account - this
 * device's included (apps/api/src/lib/fleetJoin.ts). Does nothing unless the
 * driver had already allowed notifications and the browser still holds a
 * subscription; never throws.
 */
export async function reregisterPush(): Promise<void> {
  try {
    if (pushSupport() !== 'ready' || window.Notification.permission !== 'granted') return;
    const sub = await currentSubscription();
    const json = sub?.toJSON();
    if (!sub || !json?.keys?.p256dh || !json.keys.auth) return;
    await subscribePush({
      endpoint: sub.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      user_agent: navigator.userAgent.slice(0, 400),
    });
  } catch {
    // Best effort: Settings can always re-enable.
  }
}

/**
 * Unregister this device: drop it server-side FIRST, then locally.
 *
 * Order matters. Unsubscribing locally first would leave the server
 * holding an endpoint the push service has already retired, and the
 * only thing that prunes it is a 404/410 on the next send — so the
 * driver would keep "receiving" pushes into a void until then.
 */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (sub) {
    try {
      await unsubscribePush(sub.endpoint);
    } catch {
      // Server-side removal is best-effort; a stale row prunes itself
      // on the first 404/410.
    }
    try {
      await sub.unsubscribe();
    } catch {
      // Already gone.
    }
  }
}

/**
 * "Not now" — record it so the driver is not auto-asked again.
 *
 * Server-side, because browser permission state does not sync across
 * devices and a localStorage flag dies on a cache clear: a driver who
 * declined on their phone should not be re-prompted on their tablet.
 */
export async function declinePushPrompt(): Promise<void> {
  try {
    await dismissPushPrompt();
  } catch {
    // A failed decline just means we may ask again later — annoying,
    // not broken. Never surface it.
  }
}

/**
 * Session-scoped "ask me later".
 *
 * Deliberately sessionStorage rather than the server flag: the server
 * flag means "stop asking me, on every device, forever". A driver who
 * closes the sheet without choosing has said something much weaker —
 * not this time. sessionStorage forgets on the next app launch, which
 * is exactly the intent.
 */
const SNOOZE_KEY = 'evi.push.snoozed';

export function snoozePushPrompt(): void {
  try {
    window.sessionStorage.setItem(SNOOZE_KEY, '1');
  } catch {
    // Private mode / storage disabled: worst case we ask again this
    // session. Never throw from a dismissal.
  }
}

export function isPushPromptSnoozed(): boolean {
  try {
    return window.sessionStorage.getItem(SNOOZE_KEY) === '1';
  } catch {
    return false;
  }
}
